/** @jsxImportSource @emotion/react */
'use memo'

import React, { useState } from 'react'
import css from '@emotion/css/macro'
import {
  SafetyCertificateOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import appOrigin from '@sportninja/common/constants/appOrigin'
import ShareIcon from 'src/components/icons/ShareIcon'
import ShareModal from 'src/components/ShareModal'
import Button from 'src/components/ts-components/Button'
import { ProfilePlayer } from '../types'

interface Props {
  onEditPlayer: () => void
  onOpenFamily: () => void
  player: ProfilePlayer | null
  /**
   * Whether the current user is PRIMARY (admin) for this player. Only a primary
   * may edit the player's profile, so the Player Details button is hidden
   * otherwise. Defaults to false.
   */
  canEdit?: boolean
}

const ProfilePlayerActions: React.FC<Props> = ({
  onEditPlayer,
  onOpenFamily,
  player,
  canEdit = false,
}) => {
  const [shareOpen, setShareOpen] = useState(false)

  const playerName = player
    ? `${player.name_first ?? ''} ${player.name_last ?? ''}`.trim() || 'Player'
    : 'Player'
  const shareUrl = player ? `${appOrigin}/player/${player.id}` : ''

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      `}
    >
      {/* Player Details — only a primary (admin) for this player may edit. The
          label and icon match the Player page's Player Details button. */}
      {canEdit ? (
        <Button
          text={capitalizeWords('Player Details')}
          variant='primary'
          size='compact'
          onClick={onEditPlayer}
          icon={<SafetyCertificateOutlined />}
        />
      ) : null}

      {/* Family Members — read-only list for a non-primary. A primary manages
          family from the Player Details modal instead. */}
      {!canEdit ? (
        <Button
          text={capitalizeWords('Family Members')}
          variant='outlined'
          size='compact'
          onClick={onOpenFamily}
          disabled={!player}
          icon={<UsergroupAddOutlined />}
        />
      ) : null}

      <Button
        text={capitalizeWords('Share Player')}
        variant='outlined'
        size='compact'
        iconOnly
        onClick={() => setShareOpen(true)}
        disabled={!player}
        icon={<ShareIcon size={16} />}
      />

      {/* Overflow (⋮) menu hidden until its actions are defined. To re-enable,
          add a MoreOutlined import + another iconOnly compact outlined
          Button. */}

      <ShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        entityType='player'
        entityName={playerName}
        shareUrl={shareUrl}
      />
    </div>
  )
}

export default ProfilePlayerActions
